import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const SectionTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: var(--accent-color);
    margin: 15px auto 0;
  }
`;

const CartContainer = styled.div`
  padding: 30px;
  border-radius: 8px;
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 50px 0;
`;

const CartItemsList = styled.div`
  margin-bottom: 30px;
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr auto;
  gap: 20px;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  align-items: center;
  
  @media (max-width: 576px) {
    grid-template-columns: 80px 1fr;
    grid-template-rows: auto auto;
    gap: 10px;
  }
`;

const ItemImage = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 5px;
  background-color: var(--background-color);
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 576px) {
    width: 80px;
    height: 80px;
  }
`;

const ItemInfo = styled.div`
  h3 {
    margin-bottom: 5px;
  }
  
  p {
    margin-bottom: 5px;
    font-size: 0.9rem;
    opacity: 0.8;
  }
  
  .date-location {
    font-style: italic;
  }
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  
  .price {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 10px;
  }
  
  @media (max-width: 576px) {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 10px;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--error-color);
  cursor: pointer;
  padding: 5px;
  font-size: 0.9rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const OrderSummary = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: ${props => props.total ? '1.2rem' : '1rem'};
  font-weight: ${props => props.total ? 'bold' : 'normal'};
`;

const CheckoutButton = styled(Link)`
  display: block;
  width: 300px;
  margin: 0 auto;
  padding: 15px;
  background-color: #A961ED;
  color: white;
  text-align: center;
  border-radius: 50px;
  font-weight: 600;
  margin-top: 20px;
  transition: all 0.3s ease;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ContinueShoppingLink = styled(Link)`
  color: white;
  display: block;
  text-align: center;
  margin-top: 20px;
`;

const Container = styled.div.attrs({ className: 'app-container' })`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const DateLocation = styled.p.attrs({ className: 'cart-date-location' })`
  font-size: 0.9rem;
  color: #aaa;
  margin-top: 5px;
`;

const ItemPrice = styled.div.attrs({ className: 'cart-item-price' })`
  font-size: 1.25rem;
  font-weight: 600;
  margin-left: auto;
  white-space: nowrap;
`;

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCart = () => {
      setIsLoading(true);
      try {
        // Read cart from localStorage
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          setCartItems(JSON.parse(savedCart));
        } else {
          setCartItems([]);
        }
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching cart:', err);
        setError('Failed to load your cart. Please try again.');
        setIsLoading(false);
      }
    };

    fetchCart();
  }, []);

  const handleRemoveItem = (itemId) => {
    const updatedCart = cartItems.filter(item => item.id !== itemId);
    setCartItems(updatedCart);
    // Update localStorage when removing items
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    // Dispatch custom event to notify components of cart change
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      // Consider quantity for each item
      const itemQuantity = item.quantity || 1;
      return total + (item.price * itemQuantity);
    }, 0);
  };

  const calculateTotal = () => {
    // Return the subtotal directly, no tax added
    return calculateSubtotal();
  };

  // Helper to get background name for display
  const getBackgroundName = (backgroundId) => {
    if (!backgroundId) return null;

    const types = {
      'basic-light': 'Basic Harmony (Light)',
      'basic-dark': 'Basic Harmony (Dark)',
      'sunset-pink': 'Sunset Vibe (Pink)',
      'sunset-orange': 'Sunset Vibe (Orange)',
      'pastel-pink': 'Pastel Whispers (Pink)',
      'pastel-blue': 'Pastel Whispers (Blue)',
      'pastel-white': 'Pastel Whispers (White)',
      'neon-purple': 'Neon Pop (Purple)',
      'neon-blue': 'Neon Pop (Blue)',
      'natural-gold': 'Natural Soul (Gold)',
      'natural-brown': 'Natural Soul (Brown)',
      'natural-beige': 'Natural Soul (Beige)',
      'golden-dark': 'Golden Elegance (Dark)',
      'golden-black': 'Golden Elegance (Black)',
      'golden-gold': 'Golden Elegance (Gold)'
    };

    return types[backgroundId] || null;
  };

  // Helper to get celestial elements names for display
  const getCelestialElementName = (elementId) => {
    const elements = {
      'showStars': 'Stars',
      'showConstellations': 'Constellation lines',
      'showConstellationNames': 'Constellation names',
      'showGrid': 'Coordination Grid',
      'showDeepSkyObjects': 'Deep Sky Objects',
      'showEcliptic': 'Ecliptic line'
    };

    return elements[elementId] || null;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Container>
          <CartContainer>
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              Loading your cart...
            </div>
          </CartContainer>
        </Container>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Container>
          <CartContainer>
            <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--error-color)' }}>
              {error}
            </div>
          </CartContainer>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <CartContainer>
          {cartItems.length === 0 ? (
            <EmptyCart>
              <p>Your cart is empty</p>
              <ContinueShoppingLink to="/create">Start creating your star map</ContinueShoppingLink>
            </EmptyCart>
          ) : (
            <>
              <CartItemsList>
                {cartItems.map(item => (
                  <CartItem key={item.id}>
                    <ItemImage>
                      <img src={item.imageUrl} alt={item.type === 'gift_card' ? "Gift Card" : "Star Map Preview"} />
                    </ItemImage>

                    <ItemInfo>
                      <h3>{item.name}</h3>
                      {item.type === 'star_map' && (
                        <>
                          <DateLocation>
                            {item.date} • {item.location}
                          </DateLocation>
                          {item.caption && <p>"{item.caption}"</p>}
                          <p>
                            {item.size} •
                            {item.style?.charAt(0).toUpperCase() + item.style?.slice(1)} Style
                            {item.frame !== 'none' ? ` • ${item.frame.charAt(0).toUpperCase() + item.frame.slice(1)} Frame` : ' • No Frame'}
                          </p>
                          {item.background && (
                            <p>Background: {getBackgroundName(item.background)}</p>
                          )}

                          {/* Display celestial elements if present */}
                          {(item.showStars || item.showConstellations || item.showConstellationNames ||
                            item.showGrid || item.showDeepSkyObjects || item.showEcliptic) && (
                              <div className="mt-2">
                                <p className="font-medium">Celestial Elements:</p>
                                <ul className="list-disc pl-5 mt-1">
                                  {Object.entries(item)
                                    .filter(([key, value]) => value === true && key.startsWith('show'))
                                    .map(([key]) => (
                                      <li key={key}>{getCelestialElementName(key)}</li>
                                    ))}
                                </ul>
                              </div>
                            )}
                        </>
                      )}
                      {item.type === 'gift_card' && (
                        <p>Quantity: {item.quantity}</p>
                      )}
                    </ItemInfo>

                    <ItemActions>
                      <ItemPrice>€{(item.price * (item.quantity || 1)).toFixed(2)}</ItemPrice>
                      <RemoveButton onClick={() => handleRemoveItem(item.id)}>
                        Remove
                      </RemoveButton>
                    </ItemActions>
                  </CartItem>
                ))}
              </CartItemsList>

              <OrderSummary>
                <SummaryRow>
                  <span>Subtotal:</span>
                  <span>€{calculateSubtotal().toFixed(2)}</span>
                </SummaryRow>

                <SummaryRow total>
                  <span>Total:</span>
                  <span>€{calculateTotal().toFixed(2)}</span>
                </SummaryRow>

                <CheckoutButton to="/checkout">
                  Proceed to Checkout
                </CheckoutButton>

                <ContinueShoppingLink to="/create">
                  Continue Shopping
                </ContinueShoppingLink>
              </OrderSummary>
            </>
          )}
        </CartContainer>
      </Container>
    </PageContainer>
  );
};

export default CartPage; 